import * as React from 'react'
import { createPortal } from 'react-dom'

import { PopupBlockedError } from 'errors'
import { Logger } from 'utils/logger'

export interface Props<Redirects extends boolean, Persists extends boolean | undefined> {
  // If persist on unmount is checked we can't pass children since they will be unmounted
  // even if the window stays open
  children?: Persists extends undefined ? React.ReactNode : undefined
  windowArgs: {
    url?: string
    target?: string
    features?: string
    title: string
  }
  formSubmitData?: {
    hasRedirect: Redirects
    action: string
    method?: string
    fields?: Record<string, string>
  }
  useStyles?: boolean
  // On we can't explicitly check if the window is being close or if it's redirecting
  // So if the form will redirect the page, onClose func will be called on redirect
  // NOTE -- You can just set hasRedirect to false if you are okay with the above behaviour
  onClose?: Redirects extends false ? () => void : undefined
  persistOnUnmount?: Persists
  setWindow?: (win: Window | null) => void
  // Just here to be able to force a rerender on the component to reopen the window
  shouldReRender?: boolean
}

/**
 * Not the actual window it just maintains context on the window and has some features that we use
 * for windows like submitting form data.
 *
 * Restrictions:
 *  - If `persistOnUnmount` is passed, we cannot pass `children` (see above)
 *  - If `formSubmitData.hasRedirect` is true, we cannot pass `onClose` (see above)
 *  - We will lose context on the window is the component is unmounted (eg. navigating from the page)
 *
 * Some oddities:
 *  - `shouldReRender` is the only way we can force the window to be recreated if it is closed. The value
 *    doesn't matter you just need to change the value
 *
 * Usage example:
 *  - See BsiButton
 *
 * @param {Props} props
 */
export const PopoutWindow = <
  Redirects extends boolean,
  Persists extends boolean | undefined = undefined,
>({
    windowArgs,
    children,
    formSubmitData,
    useStyles,
    onClose,
    persistOnUnmount,
    setWindow,
    shouldReRender
  }: Props<Redirects, Persists>) => {
  const [extWindow, setExtWindow] = React.useState<Window | null>(null)
  const [container, setContainer] = React.useState<HTMLDivElement | null>(null)

  const handleClose = React.useCallback(() => {
    if (onClose) {
      onClose()
    }
  }, [onClose])

  // Create initial window + container
  // https://blog.scottlogic.com/2019/10/29/popout-windows-in-react.html
  React.useEffect(() => {
    const { url, target, features } = windowArgs
    const newWindow = window.open(url, target, features)

    if (newWindow) {
      const containerEl = newWindow.document.createElement('div')
      newWindow.document.body.appendChild(containerEl)

      if (useStyles) {
        const stylesheets = Array.from(document.styleSheets)

        for (const sheet of stylesheets) {
          let newStyleEl: HTMLLinkElement | HTMLStyleElement | undefined

          if (sheet.href) {
            const newLink = newStyleEl = newWindow.document.createElement('link')
            newLink.rel = 'stylesheet'
            newLink.href = sheet.href
          } else if (sheet.cssRules && sheet.cssRules.length) {
            newStyleEl = newWindow.document.createElement('style')
            for (const rule of Array.from(sheet.cssRules)) {
              newStyleEl.appendChild(newWindow.document.createTextNode(rule.cssText))
            }
          }

          if (newStyleEl) {
            newWindow.document.head.appendChild(newStyleEl)
          }

          newWindow.document.title = windowArgs.title
        }
      }

      newWindow.addEventListener('beforeunload', handleClose)

      setExtWindow(newWindow)
      setContainer(containerEl)

      if (setWindow) {
        setWindow(newWindow)
      }
    } else {
      // Do nothing for now
      Logger.error(`${windowArgs.title} failed to open.`, new PopupBlockedError(`${windowArgs.title} failed to open.`))
    }

    return () => {
      if (!persistOnUnmount) {
        newWindow?.close()
      }
      if (setWindow) {
        setWindow(null)
      }
    }
  }, [shouldReRender]) // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (extWindow && container && formSubmitData && !extWindow.closed) {
      const { action, method, fields } = formSubmitData

      const form = extWindow.document.createElement('form')
      form.method = method || 'post'
      form.action = action

      if (fields) {
        for (const [key, value] of Object.entries(fields)) {
          const formField = extWindow.document.createElement('input')
          formField.type = 'hidden'
          formField.name = key
          formField.value = value

          form.appendChild(formField)
        }
      }

      container.appendChild(form)
      form.submit()
      form.remove()
    }
  }, [extWindow, container, formSubmitData]) // eslint-disable-line react-hooks/exhaustive-deps

  if (container) {
    return createPortal(children, container)
  }

  return null
}

export default PopoutWindow
